import dayjs, { type Dayjs } from 'dayjs';
import type { ProductShipmentLine } from '../api/types';
import { fuzzySearch, indexName, type SearchIndexEntry } from './fuzzySearch';

/**
 * Pure helpers of the «Аналитика по товарам» page: the period presets, the
 * URL ⇄ view mapping (so a накладная's «Назад» lands on the same view), the
 * product search and the drill-down grouping. No React, no locale — testable
 * in node.
 */

export type ProductPeriod = 'week' | 'month' | 'prevMonth' | 'custom';
export type ProductStatus = 'posted' | 'all';

export interface ProductsView {
  period: ProductPeriod;
  /** Inclusive business dates, YYYY-MM-DD — resolved for presets too. */
  from: string;
  to: string;
  status: ProductStatus;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const FMT = 'YYYY-MM-DD';

// dayjs core has no strict parsing (no customParseFormat plugin here), so a
// real calendar date is one that survives the round trip («2026-02-30» → Mar 2).
function isDateOnly(value: string | null): value is string {
  return value !== null && DATE_RE.test(value) && dayjs(value).format(FMT) === value;
}

/**
 * The inclusive date window of a preset, relative to `now`. The week is
 * Monday–Sunday computed by hand — the antd/dayjs locale is not loaded in
 * unit tests and must not change what a URL means.
 */
export function periodRange(
  period: Exclude<ProductPeriod, 'custom'>,
  now: Dayjs,
): { from: string; to: string } {
  const today = now.startOf('day');
  if (period === 'week') {
    const dow = (today.day() + 6) % 7; // 0 = Monday
    const monday = today.subtract(dow, 'day');
    return { from: monday.format(FMT), to: monday.add(6, 'day').format(FMT) };
  }
  const month =
    period === 'month' ? today.startOf('month') : today.subtract(1, 'month').startOf('month');
  return { from: month.format(FMT), to: month.endOf('month').format(FMT) };
}

/**
 * `?period=week|month|prevMonth|custom[&from&to][&status=all]` → view. Presets
 * are re-resolved against `now`, so a stale link still means «this week».
 * Anything malformed falls back to the current month, проведённые only.
 */
export function parseProductsSearch(search: string, now: Dayjs): ProductsView {
  const params = new URLSearchParams(search);
  const status: ProductStatus = params.get('status') === 'all' ? 'all' : 'posted';
  const period = params.get('period');
  if (period === 'custom') {
    const from = params.get('from');
    const to = params.get('to');
    if (isDateOnly(from) && isDateOnly(to) && from <= to) {
      return { period: 'custom', from, to, status };
    }
  } else if (period === 'week' || period === 'prevMonth') {
    return { period, ...periodRange(period, now), status };
  }
  return { period: 'month', ...periodRange('month', now), status };
}

/** The inverse of parseProductsSearch — a leading `?`, or '' for the default view. */
export function productsSearch(view: ProductsView): string {
  const params = new URLSearchParams();
  if (view.period !== 'month') params.set('period', view.period);
  if (view.period === 'custom') {
    params.set('from', view.from);
    params.set('to', view.to);
  }
  if (view.status === 'all') params.set('status', 'all');
  const s = params.toString();
  return s === '' ? '' : `?${s}`;
}

// Rows are immutable query data — index each name once, not per keystroke.
const nameIndex = new WeakMap<object, SearchIndexEntry>();

/**
 * Forgiving product search (see lib/fuzzySearch): typos, Latin/Cyrillic,
 * wrong keyboard layout, any word order, from one letter on. Best matches
 * first; `similarOnly` when nothing matched literally. Empty query keeps all.
 */
export function filterProducts<T extends { name: string }>(
  rows: readonly T[],
  query: string,
): { rows: T[]; similarOnly: boolean } {
  return fuzzySearch(rows, query, (row) => {
    let entry = nameIndex.get(row);
    if (!entry) {
      entry = indexName(row.name);
      nameIndex.set(row, entry);
    }
    return entry;
  });
}

export interface ProductDateGroup {
  date: string;
  lines: ProductShipmentLine[];
  quantityMilli: number;
  issueSumKopeks: number;
}

/** Drill-down lines grouped by business date, keeping the server's order. */
export function groupLinesByDate(lines: readonly ProductShipmentLine[]): ProductDateGroup[] {
  const groups: ProductDateGroup[] = [];
  for (const line of lines) {
    let group = groups[groups.length - 1];
    if (!group || group.date !== line.msDate) {
      group = { date: line.msDate, lines: [], quantityMilli: 0, issueSumKopeks: 0 };
      groups.push(group);
    }
    group.lines.push(line);
    group.quantityMilli += line.quantityMilli;
    group.issueSumKopeks += line.issueSumKopeks ?? 0;
  }
  return groups;
}

/** Наценка in basis points: profit over the buy sum of the PRICED lines
 * (mirror of the backend product-analytics.math marginBps). */
export function marginBps(profitKopeks: number, pricedBuySumKopeks: number): number | null {
  if (pricedBuySumKopeks <= 0) return null;
  return Math.round((profitKopeks * 10_000) / pricedBuySumKopeks);
}

/** Share of a part in a total, basis points; 0 when the total is empty. */
export function shareBps(partKopeks: number, totalKopeks: number): number {
  if (totalKopeks <= 0) return 0;
  return Math.round((partKopeks * 10_000) / totalKopeks);
}

/** Longest window the backend accepts (RANGE_TOO_LONG above it), in days. */
export const MAX_RANGE_DAYS = 365;

/** Basis points → percent with one decimal, Russian comma: 1234 → "12,3 %". */
export function formatBps(bps: number | null): string {
  if (bps === null) return '—';
  const sign = bps < 0 ? '-' : '';
  const abs = Math.abs(bps);
  const whole = Math.trunc(abs / 100);
  const tenth = Math.round((abs % 100) / 10);
  // 12,95 % rounds up into the next whole percent.
  return tenth === 10 ? `${sign}${whole + 1},0 %` : `${sign}${whole},${tenth} %`;
}
